import React, { useState } from "react";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { useToast } from "../hooks/use-toast";
import { MapPin, Mail, Send, Clock } from "lucide-react";
import { companyInfo } from "../data/mock";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          access_key: companyInfo.web3formsKey,
          subject: form.subject || `Website enquiry from ${form.name}`,
          from_name: "D-H Gadiola Website",
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message,
          replyto: form.email,
        }),
      });
      const data = await res.json();
      if (data && data.success) {
        toast({
          title: "Message sent",
          description: "Thanks for reaching out. We'll get back to you shortly.",
        });
        setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      } else {
        throw new Error(data?.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      toast({
        title: "Message not sent",
        description: err.message || "Network error. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <PageHero
        badge="Get In Touch"
        title="Let's talk about your project"
        subtitle="Tell us about your specifications, deadlines and environment. Our team is here to help with quotes and custom requirements."
        breadcrumbs={[{ label: "Contact" }]}
      />

      <section className="py-20" style={{ backgroundColor: "#f7faf8" }}>
        <div className="max-w-[1200px] mx-auto px-6 grid lg:grid-cols-3 gap-8">
          {/* Info cards */}
          <div className="lg:col-span-1 space-y-5">
            {[
              { icon: MapPin, title: "Office Address", value: companyInfo.address, href: null },
              { icon: Mail, title: "Email", value: companyInfo.email, href: `mailto:${companyInfo.email}` },
              { icon: WhatsAppIcon, title: "WhatsApp", value: companyInfo.whatsapp, href: companyInfo.whatsappLink, external: true },
              { icon: Clock, title: "Working Hours", value: "Mon – Fri: 8:00am – 5:30pm HST", href: null },
            ].map((c, idx) => {
              const Icon = c.icon;
              const inner = (
                <div className="bg-white rounded-2xl p-6 border border-slate-100 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-500 group">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 shadow-md shadow-emerald-500/20"
                    style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
                  >
                    <Icon className="text-white" size={20} />
                  </div>
                  <h3 className="font-bold mb-1" style={{ color: "#0a2e1f" }}>{c.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed break-words">{c.value}</p>
                </div>
              );
              if (c.href) {
                return (
                  <a
                    key={idx}
                    href={c.href}
                    {...(c.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="block"
                  >
                    {inner}
                  </a>
                );
              }
              return <div key={idx}>{inner}</div>;
            })}
            <a
              href={companyInfo.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl text-white text-sm font-semibold shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 transition-all"
              style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
            >
              <WhatsAppIcon size={16} /> Chat on WhatsApp
            </a>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="lg:col-span-2 bg-white rounded-3xl p-8 md:p-10 border border-slate-100 shadow-sm"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-2 tracking-tight" style={{ color: "#0a2e1f" }}>
              Send Us a Message
            </h2>
            <p className="text-slate-600 text-sm mb-8">We typically respond within 1 business day.</p>
            <div className="grid sm:grid-cols-2 gap-5">
              {[
                { name: "name", label: "Full Name", required: true },
                { name: "email", label: "Email", type: "email", required: true },
                { name: "phone", label: "Phone" },
                { name: "subject", label: "Subject" },
              ].map((f) => (
                <div key={f.name}>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">{f.label}</label>
                  <input
                    type={f.type || "text"}
                    name={f.name}
                    required={f.required}
                    value={form[f.name]}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
                  />
                </div>
              ))}
            </div>
            <div className="mt-5">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Message</label>
              <textarea
                name="message"
                required
                rows={6}
                value={form.message}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="mt-6 inline-flex items-center gap-2 px-8 py-3.5 text-white text-sm font-semibold rounded-full shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/50 hover:scale-[1.02] transition-all disabled:opacity-60"
              style={{ background: "linear-gradient(135deg, #10b981 0%, #059669 100%)" }}
            >
              {submitting ? "Sending..." : (<><Send size={15} /> Send Message</>)}
            </button>
          </form>
        </div>
      </section>

      {/* Map */}
      <section className="pb-20" style={{ backgroundColor: "#f7faf8" }}>
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="rounded-3xl overflow-hidden border border-slate-100 shadow-sm h-[400px]">
            <iframe
              title="D-H Gadiola Office Location"
              src="https://www.google.com/maps?q=2845+Round+Top+Drive+Honolulu+HI+96822&output=embed"
              className="w-full h-full border-0"
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Contact;
